import { type Theme } from '@emotion/react'
import { type CSSInterpolation } from '@emotion/serialize'
import { type Interpolation } from '@emotion/styled/dist/emotion-styled.cjs'
import {
  Box,
  type BoxProps,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

import Icon from 'components/Icon'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'

/**
 * Provides user controls for expanding/collapsing child content
 */
export const CollapsibleBox: React.FC<
  {
    width?: string
    height?: string
    collapsed?: boolean
    contentStyles?: CSSInterpolation
  } & BoxProps
> = ({
  width = '75vw',
  height = '50vh',
  collapsed = true,
  contentStyles = {},
  children,
  ...rest
}) => {
  const { trans } = useTranslation()
  const [isCollapsed, setIsCollapsed] = useBoolean(collapsed)
  const handleSetCollapsed = (
    _event: React.MouseEvent<HTMLElement>,
    collapsed: boolean
  ) => {
    setIsCollapsed(collapsed)
  }
  const collapsedStyling: Interpolation<Theme> = {
    // make the container constrained and scrollable because concurrent trees can be nested quite deep and render widely
    maxWidth: width,
    maxHeight: height,
    overflow: 'scroll',
    // add an inset drop shadow to help indicate scrollable content
    boxShadow: `inset 1px 1px 2px 1px ${theme.colors.gray[2]}`,
    // add padding at the top to drop the content below the shadow
    paddingTop: theme.space[2],
    paddingBottom: theme.space[2]
  }

  return (
    <Stack spacing={2} {...rest}>
      <ToggleButtonGroup
        exclusive
        aria-label={trans('collapse_expand')}
        size="small"
        value={isCollapsed}
        onChange={handleSetCollapsed}
      >
        <ToggleButton aria-label={trans('expanded')} value={false}>
          <Icon icon="expand" />
        </ToggleButton>
        <ToggleButton aria-label={trans('collapsed')} value={true}>
          <Icon icon="compress" />
        </ToggleButton>
      </ToggleButtonGroup>

      <Box css={[contentStyles, isCollapsed && collapsedStyling]}>
        {children}
      </Box>
    </Stack>
  )
}
